// import external libraries
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

// import hooks
import { queries, requests } from 'RestAPIs/rapini';

// import utils
import { entityFiltersForView, toCategoryFilter } from './Backlog.utils';

// import constants
import { BACKLOG_ACTIVITY_SCHEDULING_LABEL } from './Backlog.consts';

// import types
import type {
  BackLogResponseList,
  PlanningEntityType,
  SchedulingLabel
} from 'generated/index';
import type {
  BacklogEntityFilter,
  BacklogTaskCategory,
  FilterOption
} from './Backlog.types';

type UseGetBacklogParams = {
  startDate: Date;
  endDate: Date;
  includeAllSummaries: boolean;
  entityFilters: BacklogEntityFilter[];
  activityIds: string[];
  /** The tiles the planner has on — the backend detects only these. */
  activeCategories: BacklogTaskCategory[];
  /** Which planning entity's rows to report on — one type at a time. */
  rowEntityType: PlanningEntityType;
  /**
   * True while the page has nothing worth asking yet: a view still missing the filter it cannot work
   * without (see missingRequiredFilter), or a remembered activity filter whose activities have not come in
   * to be resolved against. Decided by the page rather than here, so what it shows and what it withholds
   * can never disagree.
   */
  isRequestHeld: boolean;
};

/**
 * Reads the backlog timeline for every care receiver that has a prescription in the period.
 *
 * `getBacklogTimeline` is a POST — rapini therefore generates it as a mutation — but it is
 * semantically a read, so it is wrapped in `useQuery` here.
 *
 * No `planningEntities` are sent when nothing is picked: the backend resolves the
 * care receivers itself, in one query, narrowed to those that actually have a prescription
 * overlapping the period. When filters are set, they narrow that same resolution.
 *
 * `includeAllSummaries: false` asks the backend for deviating weeks only — shortages and surpluses
 * alike — and it then also leaves out rules and care receivers that have nothing to report.
 */
export const useGetBacklog = ({
  startDate,
  endDate,
  includeAllSummaries,
  entityFilters,
  activityIds,
  activeCategories,
  rowEntityType,
  isRequestHeld
}: UseGetBacklogParams) => {
  const start = dayjs(startDate).format('YYYY-MM-DD');
  const end = dayjs(endDate).format('YYYY-MM-DD');

  // Only the active view's picks narrow the list — see entityFiltersForView.
  const viewEntityFilters = entityFiltersForView(entityFilters, rowEntityType);

  // Stable, order-independent serializations so the query key only changes when the filter
  // contents actually change, not on every array identity.
  const entityKey = viewEntityFilters
    .map((f) => `${f.planningEntityType}:${f.entityId}`)
    .sort()
    .join(',');
  const activityKey = [...activityIds].sort().join(',');
  // The selection has to be part of the key, or switching a tile would be served the response fetched
  // for the previous selection — rows for a category that is now off.
  const categoryKey = [...activeCategories].sort().join(',');

  // No onError snackbar: Backlog.tsx already renders an inline error state with a retry button for
  // isError, which is discoverable and actionable where a snackbar just disappears.
  return useQuery<BackLogResponseList>(
    [
      'backlog',
      start,
      end,
      includeAllSummaries,
      entityKey,
      activityKey,
      categoryKey,
      rowEntityType
    ],
    () =>
      requests.getBacklogTimeline({
        period: { start, end },
        includeAllSummaries,
        planningEntities: viewEntityFilters.map((f) => ({
          entityId: f.entityId,
          planningEntityType: f.planningEntityType
        })),
        activityIds,
        categories: toCategoryFilter(activeCategories),
        planningEntityType: rowEntityType
      }),
    {
      refetchOnWindowFocus: false,
      // Nothing selected means nothing to show, so there is nothing worth asking for — and a view that is
      // not scoped yet has not been asked a question worth sending.
      enabled: activeCategories.length > 0 && !isRequestHeld
    }
  );
};

/** The activity list, with the loading flag the page needs to tell "no activities" from "not in yet". */
type BacklogActivityOptions = {
  options: FilterOption[];
  isLoading: boolean;
};

/**
 * Every activity the tenant plans with, as filter options.
 *
 * Read from the activities endpoint rather than from the takenlijst's own response, which is what the filter
 * used to be built from. That cost a second, deliberately unnarrowed backlog fetch — without it the options
 * collapsed to the activity already picked, and a planner could not add a second one. The activities are
 * master data, so asking for them directly is both lighter on the backend and a complete list: an activity
 * nobody has a task for is now offered too, and picking it simply reports nothing.
 *
 * `MRP` is not decoration: with no scheduling label the endpoint answers with an empty list, and MRP is the
 * label that returns the planning activities the takenlijst's prescriptions are written in.
 */
export const useBacklogActivityOptions = (): BacklogActivityOptions => {
  // Without this the mapping below hands back a new array every render, and every useMemo downstream of
  // activityOptions — the selected options, the row filter, the built rows — recomputes with it.
  'use memo';

  const { data = [], isLoading } = queries.useGetActivities(
    // The endpoint takes a comma-separated string of labels where the generated client types an array,
    // hence the cast — the same shape the entity search and the appointment form send.
    BACKLOG_ACTIVITY_SCHEDULING_LABEL as unknown as SchedulingLabel[]
  );

  return {
    options: data.map(({ entityId, code, name }) => ({
      id: entityId,
      // "{name} - {code}", the way the filter has always named an activity.
      name: `${name} - ${code}`
    })),
    isLoading
  };
};
